import React from "react";
import { View } from "react-native";
import { Dropdown } from "react-native-element-dropdown";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import Text from "@/components/shared/AppText";
import { t, tc, tn } from "@/localization";
import { forNoun, groupList, roomLabelOf, roomNounOf } from "./rooms";

const ADD_ROOM = "__add_room__";

// The add item and the pill ("Stage 1 · 3 stages") for each noun, so a
// translation can agree with it.
const MENU_WORDS = {
  Room: () => ({
    add: t("Add room"),
    count: (label, count) =>
      tn("{label} · {count} room", "{label} · {count} rooms", count, {
        label,
      }),
  }),
  Stage: () => ({
    add: t("Add stage"),
    count: (label, count) =>
      tn("{label} · {count} stage", "{label} · {count} stages", count, {
        label,
      }),
  }),
  Track: () => ({
    add: t("Add track"),
    count: (label, count) =>
      tn("{label} · {count} track", "{label} · {count} tracks", count, {
        label,
      }),
  }),
  Court: () => ({
    add: t("Add court"),
    count: (label, count) =>
      tn("{label} · {count} court", "{label} · {count} courts", count, {
        label,
      }),
  }),
  Field: () => ({
    add: t("Add field"),
    count: (label, count) =>
      tn("{label} · {count} field", "{label} · {count} fields", count, {
        label,
      }),
  }),
  Hall: () => ({
    add: t("Add hall"),
    count: (label, count) =>
      tn("{label} · {count} hall", "{label} · {count} halls", count, {
        label,
      }),
  }),
  Camera: () => ({
    add: t("Add another camera"),
    count: (label, count) =>
      tn("{label} · {count} camera", "{label} · {count} cameras", count, {
        label,
      }),
  }),
};

const LiveDot = ({ style }) => (
  <View
    style={[
      { width: 8, height: 8, borderRadius: 4, backgroundColor: "#ef4444" },
      style,
    ]}
  />
);

// Dashboard card: a pill over the cover image ("Stage 1 · 3 stages", red dot
// while any room is live) that drops down the event's rooms. Picking one opens
// it; the last item adds a room. Keeps every card the same height.
const RoomMenu = ({ primary, rooms, onSelect, onAdd, style }) => {
  const members = groupList(primary, rooms);
  if (members.length < 2) return null;
  const words = forNoun(MENU_WORDS, roomNounOf(primary));
  const anyLive = members.some((m) => m.event_status === "live");
  const data = [
    ...members.map((m) => ({
      value: m.slug,
      label: roomLabelOf(m),
      member: m,
    })),
    ...(onAdd ? [{ value: ADD_ROOM, label: words.add }] : []),
  ];

  // The pill sizes to its label, up to the cover's width. The library gives
  // the label flex: 1, a zero basis, so the pill used to stay at minWidth and
  // a longer label wrapped: on iOS "Camera 1 · 2 cameras" showed as
  // "Camera 1 · 2 camera" with the chevron below the text.
  return (
    <View
      pointerEvents="box-none"
      style={[tw`absolute top-2 left-2 right-2 items-start`, style]}
    >
      <Dropdown
        data={data}
        labelField="label"
        valueField="value"
        value={null}
        placeholder={words.count(roomLabelOf(primary), members.length)}
        onChange={(item) =>
          item.value === ADD_ROOM ? onAdd && onAdd() : onSelect(item.member)
        }
        maxHeight={280}
        style={{
          height: 30,
          minWidth: 150,
          maxWidth: "100%",
          paddingLeft: 10,
          paddingRight: 6,
          borderRadius: 15,
          backgroundColor: "rgba(17, 24, 39, 0.78)",
        }}
        placeholderStyle={{
          ...tw`text-white text-xs font-semibold`,
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: "auto",
        }}
        selectedTextProps={{ numberOfLines: 1 }}
        iconColor="#ffffff"
        iconStyle={{ width: 16, height: 16 }}
        containerStyle={{ width: 220, borderRadius: 10, overflow: "hidden" }}
        renderLeftIcon={() => (anyLive ? <LiveDot style={tw`mr-1.5`} /> : null)}
        renderItem={(item) =>
          item.value === ADD_ROOM ? (
            <View
              style={tw`flex-row items-center px-3 py-2.5 border-t border-gray-100`}
            >
              <Ionicons name="add" size={16} color="#374151" />
              <Text style={tw`text-sm text-gray-700 ml-1.5`}>{item.label}</Text>
            </View>
          ) : (
            <View style={tw`flex-row items-center px-3 py-2.5`}>
              {item.member.event_status === "live" ? (
                <LiveDot style={tw`mr-2`} />
              ) : (
                <View style={tw`w-2 mr-2`} />
              )}
              <Text style={tw`text-sm font-semibold text-gray-900`}>
                {item.label}
              </Text>
              {!item.member.room_of && (
                <Text style={tw`text-xs text-gray-500 ml-1`}>
                  {`· ${tc("room", "Main")}`}
                </Text>
              )}
              <View style={tw`flex-1`} />
              {!item.member.is_published && (
                <Ionicons name="lock-closed" size={12} color="#6b7280" />
              )}
            </View>
          )
        }
      />
    </View>
  );
};

export default RoomMenu;
